import {useRoute, useRouter} from "vue-router";
import type {EditorType} from "./useEditorResolver.ts";
import { getWidgetConfig } from "../runtime/widgetConfig";

export function useEditorNav() {
    let router: ReturnType<typeof useRouter> | null = null
    try {
        router = useRouter()
    } catch {
        router = null
    }
    let route: ReturnType<typeof useRoute> | null = null
    try {
        route = useRoute()
    } catch {
        route = null
    }

    const currentQuery = () => {
        const q = (route as any)?.query
        return q && typeof q === 'object' ? q : {}
    }

    function resolveEditorType(input?: any): EditorType {
        // Priorität: explizit input -> fallback query -> fallback 'single'
        const t = (input?.editorType ?? input ?? (currentQuery() as any).editorType) as any
        if (t === 'design') return 'design'
        return t === 'multi' ? 'multi' : 'single'
    }

    function editorPath(type: EditorType) {
        return type === 'design' ? '/design' : '/multi'
    }

    function buildEditorHref(path: string, query: Record<string, any>) {
        const cfg = getWidgetConfig() as any
        const configuredBase = String(cfg?.editorBaseUrl || '').trim().replace(/\/+$/, '')
        const base = configuredBase || '/editor'
        const search = new URLSearchParams()
        for (const [key, value] of Object.entries(query || {})) {
            if (value == null || value === '') continue
            search.set(key, String(value))
        }
        const qs = search.toString()
        const hashTarget = qs ? `${path}?${qs}` : path
        return base ? `${base}#${hashTarget}` : `#${hashTarget}`
    }

    async function goToEditor(opts: {
        type?: EditorType | any
        categoryId?: number
        productId?: number
        projectCode?: string
        imageId?: string
        imageIds?: string[] // selected
    }) {
        const type = resolveEditorType(opts.type)
        const firstImageId = String(opts.imageId || opts.imageIds?.[0] || '').trim()
        const projectCode = String(opts.projectCode || '').trim()

        const query: Record<string, any> = {
            ...currentQuery(),
            editorType: type,
        }

        delete query.imageIds
        delete query.imageId

        if (Number(opts.categoryId || 0) > 0) {
            query.category = String(opts.categoryId)
            query.categoryId = String(opts.categoryId)
        }

        if (Number(opts.productId || 0) > 0) {
            query.product = String(opts.productId)
            query.productId = String(opts.productId)
        }

        if (projectCode) {
            query.projectCode = projectCode
        }

        if (firstImageId) query.imageId = firstImageId

        if (router && typeof (router as any).push === 'function') {
            await router.push({ path: editorPath(type), query })
            return
        }

        if (typeof window !== 'undefined') {
            window.location.href = buildEditorHref(editorPath(type), query)
        }
    }

    return { goToEditor, resolveEditorType }
}
